import { toKebabCase } from "Infrastructure/Utils/UrlUtils";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AnalysisDetailSection } from "../../Components/Analyses/Detail/MultilevelTabMenu";

type UseAnalysisDetailSectionResult = {
  section: AnalysisDetailSection;
  setSection: (section: AnalysisDetailSection) => void;
};

const ENUM_KEYS = Object.keys(AnalysisDetailSection);

const sectionToSlug = (s: AnalysisDetailSection): string => {
  const key = AnalysisDetailSection[s] as any as string;

  return toKebabCase(key);
};

const mapSlugToSection = (slug: string | null | undefined, fallback: AnalysisDetailSection): AnalysisDetailSection => {
  if (!slug) {
    return fallback;
  }
  const matchKey = ENUM_KEYS.find((k) => toKebabCase(k) === slug);

  return matchKey ? (AnalysisDetailSection as any)[matchKey] : fallback;
};

export const useAnalysisDetailSection = (
  defaultSection: AnalysisDetailSection = AnalysisDetailSection.Family,
): UseAnalysisDetailSectionResult => {
  const [searchParams, setSearchParams] = useSearchParams();

  const section = useMemo(() => {
    return mapSlugToSection(searchParams.get("tab"), defaultSection);
  }, [searchParams, defaultSection]);

  useEffect(() => {
    const currentSlug = searchParams.get("tab");
    if (!currentSlug) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", sectionToSlug(defaultSection));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, defaultSection, setSearchParams]);

  const setSection = (newSection: AnalysisDetailSection) => {
    const currentSlug = searchParams.get("tab");
    const nextSlug = sectionToSlug(newSection);

    if (currentSlug !== nextSlug) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", nextSlug);
      setSearchParams(next, { replace: true });
    }
  };

  return { section, setSection };
};
